import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { 
  ClockIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const FrequencyForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    frequency_name: '',
    display_label: '',
    days_interval: '',
    description: '',
    sort_order: 1,
    is_active: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Common frequency presets
  const frequencyPresets = [
    { name: 'one-time', label: 'One-time', days: 0, description: 'Single payment, no recurring billing' },
    { name: 'daily', label: 'Daily', days: 1, description: 'Billed every day' },
    { name: 'weekly', label: 'Weekly', days: 7, description: 'Billed every week' },
    { name: 'bi-weekly', label: 'Bi-weekly', days: 14, description: 'Billed every two weeks' },
    { name: 'monthly', label: 'Monthly', days: 30, description: 'Billed every month' },
    { name: 'quarterly', label: 'Quarterly', days: 90, description: 'Billed every three months' },
    { name: 'semi-annually', label: 'Semi-annually', days: 180, description: 'Billed every six months' },
    { name: 'annually', label: 'Annually', days: 365, description: 'Billed every year' }
  ];

  useEffect(() => {
    if (isEditing) {
      fetchFrequency();
    }
  }, [id]);

  const fetchFrequency = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/price-book/frequencies/${id}`);
      const frequency = response.data;
      
      setFormData({
        frequency_name: frequency.frequency_name || '',
        display_label: frequency.display_label || '',
        days_interval: frequency.days_interval || '',
        description: frequency.description || '',
        sort_order: frequency.sort_order || 1,
        is_active: frequency.is_active !== false
      });
    } catch (err) {
      console.error('Error fetching frequency:', err);
      setError('Failed to load frequency data');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.frequency_name.trim()) {
      errors.frequency_name = 'Frequency name is required';
    }

    if (!formData.display_label.trim()) {
      errors.display_label = 'Display label is required';
    }

    if (!formData.days_interval && formData.days_interval !== 0) {
      errors.days_interval = 'Days interval is required';
    } else if (formData.days_interval < 0) {
      errors.days_interval = 'Days interval cannot be negative';
    }

    if (formData.sort_order < 1) {
      errors.sort_order = 'Sort order must be at least 1';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = {
        ...formData,
        days_interval: parseInt(formData.days_interval),
        sort_order: parseInt(formData.sort_order)
      };

      if (isEditing) {
        await api.put(`/price-book/frequencies/${id}`, submitData);
      } else {
        await api.post('/price-book/frequencies', submitData);
      }

      navigate('/price-book/frequencies');
    } catch (err) {
      console.error('Error saving frequency:', err);
      setError(err.response?.data?.error || 'Failed to save frequency');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePresetSelect = (preset) => {
    setFormData(prev => ({
      ...prev,
      frequency_name: preset.name,
      display_label: preset.label,
      days_interval: preset.days,
      description: preset.description
    }));
    
    // Clear any validation errors
    setValidationErrors({});
  };

  if (loading && isEditing) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/price-book/frequencies')}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isEditing ? 'Edit Billing Frequency' : 'Add New Billing Frequency'}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {isEditing ? 'Update frequency information' : 'Create a new billing frequency'}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Frequency Presets */}
      {!isEditing && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Start - Common Frequencies</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {frequencyPresets.map((preset) => (
              <button
                key={preset.name}
                type="button"
                onClick={() => handlePresetSelect(preset)}
                className="text-left p-2 text-xs border border-gray-200 rounded hover:bg-white hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <div className="font-medium text-gray-900">{preset.label}</div>
                <div className="text-gray-500">{preset.days} days</div>
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Click a preset to auto-fill the form, or create a custom frequency below
          </p>
        </div>
      )}

      {/* Form */}
      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Frequency Name */}
            <div>
              <label htmlFor="frequency_name" className="block text-sm font-medium text-gray-700">
                Frequency Name *
              </label>
              <input
                type="text"
                name="frequency_name"
                id="frequency_name"
                value={formData.frequency_name}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.frequency_name ? 'border-red-300' : ''
                }`}
                placeholder="e.g., monthly, quarterly, one-time"
              />
              {validationErrors.frequency_name && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.frequency_name}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Internal name used for system identification (lowercase, hyphens allowed)
              </p>
            </div>

            {/* Display Label */}
            <div>
              <label htmlFor="display_label" className="block text-sm font-medium text-gray-700">
                Display Label *
              </label>
              <input
                type="text"
                name="display_label"
                id="display_label"
                value={formData.display_label}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.display_label ? 'border-red-300' : ''
                }`}
                placeholder="e.g., Monthly, Quarterly, One-time"
              />
              {validationErrors.display_label && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.display_label}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                User-friendly name displayed in the interface
              </p>
            </div>

            {/* Days Interval */}
            <div>
              <label htmlFor="days_interval" className="block text-sm font-medium text-gray-700">
                Days Interval *
              </label>
              <input
                type="number"
                name="days_interval"
                id="days_interval"
                min="0"
                value={formData.days_interval}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.days_interval ? 'border-red-300' : ''
                }`}
                placeholder="e.g., 30, 90, 365"
              />
              {validationErrors.days_interval && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.days_interval}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Number of days between billing cycles (0 = one-time)
              </p>
            </div>

            {/* Sort Order */}
            <div>
              <label htmlFor="sort_order" className="block text-sm font-medium text-gray-700">
                Sort Order
              </label>
              <input
                type="number"
                name="sort_order"
                id="sort_order"
                min="1"
                value={formData.sort_order}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.sort_order ? 'border-red-300' : ''
                }`}
              />
              {validationErrors.sort_order && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.sort_order}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Controls the display order (lower numbers appear first)
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              name="description"
              id="description"
              rows={3}
              value={formData.description}
              onChange={handleChange}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Optional description of this billing frequency..."
            />
            <p className="mt-1 text-xs text-gray-500">
              Optional description to help users understand this frequency
            </p>
          </div>

          {/* Active Status */}
          <div className="flex items-center">
            <input
              id="is_active"
              name="is_active"
              type="checkbox"
              checked={formData.is_active}
              onChange={handleChange}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
              Active
            </label>
            <p className="ml-2 text-xs text-gray-500">
              Inactive frequencies are hidden from selection lists
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/price-book/frequencies')}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <ClockIcon className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update Frequency' : 'Create Frequency'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FrequencyForm; 